import React, { useState, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import { FiServer, FiKey, FiHardDrive } from "react-icons/fi";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  IconButton,
  createTheme,
  ThemeProvider,
  CssBaseline,
} from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

// Create motion-enabled Box
const MotionBox = motion(Box);

export default function Login({ setToken }) {
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState("");
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark" || false
  );

  // Memoized theme
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: { main: "#6366f1" },
          background: {
            default: darkMode ? "#0f172a" : "#f9fafb",
            paper: darkMode ? "rgba(30,41,59,0.85)" : "rgba(255,255,255,0.85)",
          },
        },
        typography: { fontFamily: "'Inter','Roboto',sans-serif" },
        shape: { borderRadius: 16 },
      }),
    [darkMode]
  );

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    localStorage.setItem("theme", !darkMode ? "dark" : "light");
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const resp = await axios.post(
        "https://api.harishacrypto.xyz/service18/api/auth/token",
        { api_key: apiKey }
      );

      setToken(resp.data.token);
      localStorage.setItem("jwt", resp.data.token);

      Swal.fire({
        title: "Login Successful 🎉",
        text: "Welcome back, Admin!",
        icon: "success",
        confirmButtonText: "Continue",
        confirmButtonColor: "#4f46e5",
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const floatVariants = {
    float: {
      y: [0, -20, 0],
      transition: { repeat: Infinity, duration: 4, ease: "easeInOut" },
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          background: darkMode
            ? "linear-gradient(135deg,#0f172a,#1e293b,#000)"
            : "linear-gradient(135deg,#f9fafb,#e2e8f0,#cbd5e1)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Floating Animated Icons */}
        <motion.div
          variants={floatVariants}
          animate="float"
          style={{
            position: "absolute",
            top: 60,
            left: 40,
            fontSize: "4rem",
            color: "rgba(99,102,241,0.3)",
          }}
        >
          <FiServer />
        </motion.div>
        <motion.div
          variants={floatVariants}
          animate="float"
          style={{
            position: "absolute",
            top: "30%",
            right: 80,
            fontSize: "3.5rem",
            color: "rgba(168,85,247,0.3)",
          }}
        >
          <FiKey />
        </motion.div>
        <motion.div
          variants={floatVariants}
          animate="float"
          style={{
            position: "absolute",
            bottom: 100,
            left: "35%",
            fontSize: "5rem",
            color: "rgba(236,72,153,0.25)",
          }}
        >
          <FiHardDrive />
        </motion.div>

        {/* Login Card with MotionBox */}
        <MotionBox
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 5,
              borderRadius: 4,
              width: "100%",
              maxWidth: 400,
              backdropFilter: "blur(18px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
              position: "relative",
            }}
          >
            {/* Theme Toggle */}
            <IconButton
              onClick={toggleTheme}
              sx={{ position: "absolute", top: 16, right: 16 }}
              color="inherit"
            >
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>

            <Typography variant="h4" align="center" fontWeight={700} gutterBottom>
              🔐 Admin Login
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={submit}>
              <TextField
                fullWidth
                variant="outlined"
                label="API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your secure API key"
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  py: 1.5,
                  fontWeight: "bold",
                  background: "linear-gradient(90deg,#6366f1,#a855f7,#ec4899)",
                  boxShadow: "0px 4px 12px rgba(99,102,241,0.4)",
                  "&:hover": {
                    opacity: 0.9,
                    boxShadow: "0px 6px 18px rgba(99,102,241,0.6)",
                  },
                }}
              >
                Login
              </Button>
            </form>

            <Typography
              variant="caption"
              display="block"
              align="center"
              sx={{ mt: 4, color: "text.secondary" }}
            >
              Protected dashboard – authorized users only
            </Typography>
          </Paper>
        </MotionBox>
      </Box>
    </ThemeProvider>
  );
}
